import { concatBasePathWithPath } from 'common/router/utils';
import { documentBySegment } from 'features/charity-vetting/country-specific-flows/fr-flow/frDocuments';
import { type CharityDocument } from 'features/documents/types';

import { getStepNameFromPath, vettingPath } from '../common-flow/entries/path';
import { type FormatMessage, getPageSchema } from '../common-flow/validation/pageSchemaRegistry';
import {
  CompletionStatus,
  type CountryCode,
  type VettingRequestData,
} from '../types/charityVettingTypes';

import { getFlowSequence, getNextStepSegment } from './charityVettingFlow';
import { fromCharityDocuments, fromVettingRequestData } from './vettingApiPayload';

const GET_STARTED_PATH = concatBasePathWithPath(vettingPath.getStartedPath, vettingPath.basePath);
const REVIEW_PATH = concatBasePathWithPath(vettingPath.reviewDetailsPath, vettingPath.basePath);

const TRANSIENT_ANSWER_STEPS = new Set<string>([
  vettingPath.pliPath, // hasPli
  vettingPath.rgseaaQuestionPath, // hasRgseaa
]);

/**
 * Given the persisted vetting request, returns the route path where the user
 * should resume:
 * - no request → get-started;
 * - SUBMITTED → null (the caller routes to the progress page);
 * - otherwise → the first step on this charity's own walk (getFlowSequence)
 *   whose per-page schema does NOT pass against the saved data, or the review
 *   step when every visited step is complete but not yet submitted.
 *
 * Because the walk simulates the user's actual route through the wizard, steps
 * on branches the charity never takes are never even considered — they cannot
 * become the resume target, and their schemas need no branch guards.
 *
 * The walk starts after legal-representative: the charity record is created on
 * the contact step and the search/contact/legal-rep answers either aren't
 * persisted (search query, legal-rep role) or are implied complete by the
 * record's existence.
 */
export const resolveResumeStep = (
  data: VettingRequestData | undefined,
  country: CountryCode,
  formatMessage: FormatMessage,
  documents: CharityDocument[] = [],
  areDocumentsUnknown = false,
): string | null => {
  if (!data) {
    return GET_STARTED_PATH;
  }
  if (data.completionStatus === CompletionStatus.SUBMITTED) {
    return null;
  }

  const charityOnboarding = {
    ...fromVettingRequestData(data),
    vettingDocuments: fromCharityDocuments(documents),
  };
  const firstOrgStep = getNextStepSegment(
    vettingPath.legalRepresentativePath,
    charityOnboarding,
    country,
  );
  if (!firstOrgStep) {
    return GET_STARTED_PATH;
  }

  const walk = getFlowSequence(charityOnboarding, country, firstOrgStep);
  const firstIncomplete = walk.find((segment) => {
    if (segment === vettingPath.reviewDetailsPath) {
      return false;
    }
    if (TRANSIENT_ANSWER_STEPS.has(segment)) {
      return false;
    }
    if (areDocumentsUnknown && documentBySegment(segment)) {
      return false;
    }
    const stepName = getStepNameFromPath(segment);
    return !getPageSchema(stepName, formatMessage, country).safeParse({ charityOnboarding })
      .success;
  });

  return firstIncomplete
    ? concatBasePathWithPath(firstIncomplete, vettingPath.basePath)
    : REVIEW_PATH;
};
